import React, { useCallback, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { ItemType } from "../models/ItemTypes";
import { ItemModel } from "../models/ItemModelTypes";
import { ItemCategory } from "../models/ItemCategoryTypes";
import { UOM } from "../models/uomtypes";
import "../styles/AddItemForm.css";
import { fetchDropDownData } from "../utils/fetchDropDownData";
import { debounce } from "lodash";
import { ItemListUtils } from "../utils/itemlist/itemListUtils";
import {AddItemFormProps} from '../models/itemform/types'

const AddItemForm: React.FC<AddItemFormProps> = ({
  onSave,
  onCancel,
  isProcessing,
  isEditing,
  editingItem,
}) => {
  const {
    fetchModels,
    fetchCategories,
    fetchUoms,
    modelOptions,
    categoryOptions,
    uomOptions,
    setModelOptions,
    setCategoryOptions,
    setUomOptions,
  } = ItemListUtils();

  const [item, setItem] = useState<ItemType>({
    hsnCode: "",
    partName: "",
    internalPartNo: "",
    externalPartNo: "",
    model: "",
    category: "",
    uom: "",
    purchaseItem: false,
    sellingItem: false,
    productionItem: false,
    stockPerDay: 0,
    packingQty: 0,
    safetyDays: 0,
    minReqDay: 0,
    maxReqDays: 0,
    selfLife: false,
    expiryDays: 0,
    scheduleNo: "0",
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [shouldAutoPopulateExternal, setShouldAutoPopulateExternal] =
    useState<boolean>(true);
  const [shouldEnableAutoPopulate, setShouldEnableAutoPopulate] =
    useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModel, setSelectedModel] = useState<{label: string, value: string} | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<{label: string, value: string} | null>(null);
  const [selectedUom, setSelectedUom] = useState<{label: string, value: string} | null>(null);

  useEffect(() => {
    fetchDropDownData({
      endpoint: "/itemmodels",
      labelKey: "name",
      valueKey: "_id",
    })
      .then((data) => {
        console.log("Model data:", data);
        setModelOptions(data);
      })
      .catch((err) => {
        console.error("Error fetching item models:", err);
      });

    fetchDropDownData({
      endpoint: "/itemcategorys",
      labelKey: "name",
      valueKey: "_id",
    }).then(setCategoryOptions);

    fetchDropDownData({
      endpoint: "/uoms",
      labelKey: "name",
      valueKey: "_id",
    }).then(setUomOptions);
  }, []);

  useEffect(() => {
    if (isEditing && editingItem) {
      console.log(editingItem);
  
      const modelObj = typeof editingItem.model === 'object' && 'status' in editingItem.model && editingItem.model.status === 'ACTIVE'
        ? editingItem.model
        : null;
  
      const categoryObj = typeof editingItem.category === 'object' && 'status' in editingItem.category && editingItem.category.status === 'ACTIVE'
        ? editingItem.category
        : null;
  
      const uomObj = typeof editingItem.uom === 'object' && 'status' in editingItem.uom && editingItem.uom.status === 'ACTIVE'
        ? editingItem.uom
        : null;
  
      if (modelObj) {
        setSelectedModel({
          label: modelObj.name,
          value: modelObj._id,
        });
      }

      if(categoryObj){
        setSelectedCategory({
          label: categoryObj.name,
          value: categoryObj._id
        });
      }

      if(uomObj){
        setSelectedUom({
          label: uomObj.name,
          value: uomObj._id
        });
      }
  
      setItem({
        ...editingItem,
        model: modelObj ? modelObj._id : "",
        category: categoryObj ? categoryObj._id : "",
        uom: uomObj ? uomObj._id : "",
      });
  
      setShouldAutoPopulateExternal(false);
      setShouldEnableAutoPopulate(false);
    } else {
      
      setItem({
        hsnCode: "",
        partName: "",
        internalPartNo: "",
        externalPartNo: "",
        model: "",
        category: "",
        uom: "",
        purchaseItem: false,
        sellingItem: false,
        productionItem: false,
        stockPerDay: 0,
        packingQty: 0,
        safetyDays: 0,
        minReqDay: 0,
        maxReqDays: 0,
        selfLife: false,
        expiryDays: 0,
        scheduleNo: "0",
        status: "ACTIVE",
      });
      
      setSelectedModel(null);
      setSelectedCategory(null);
      setSelectedUom(null);
  
      setShouldAutoPopulateExternal(true);
      setShouldEnableAutoPopulate(true);
    }
  
    setErrors({});
    setIsSubmitting(false);
  }, [isEditing, editingItem]);
  
  useEffect(() => {
    if (!isProcessing && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isProcessing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (name === "hsnCode") {
      if (value.length <= 8) {
        setItem((prev) => ({
          ...prev,
          hsnCode: value,
        }));
        setErrors((prev) => ({ ...prev, hsnCode: "" }));
      }
    } else if (name === "internalPartNo") {
      const formattedValue = value.toUpperCase();
      if (formattedValue.length <= 15) {
        if (!isEditing && shouldEnableAutoPopulate) {
          setItem((prev) => ({
            ...prev,
            internalPartNo: formattedValue,
            externalPartNo: shouldAutoPopulateExternal
              ? formattedValue
              : prev.externalPartNo,
          }));
        } else {
          setItem((prev) => ({
            ...prev,
            internalPartNo: formattedValue,
          }));
        }
        setErrors((prev) => ({
          ...prev,
          internalPartNo: "",
          externalPartNo: "",
        }));
      }
    } else if (name === "externalPartNo") {
      setShouldAutoPopulateExternal(false);
      const formattedValue = value.toUpperCase();
      if (formattedValue.length <= 15) {
        setItem((prev) => ({
          ...prev,
          externalPartNo: formattedValue,
        }));
        setErrors((prev) => ({ ...prev, externalPartNo: "" }));
      }
    } else if (type === "number") {
      if (!isEditing && shouldEnableAutoPopulate) {
        setShouldAutoPopulateExternal(false);
      }
      const limitedNumericFields = [
        "stockPerDay",
        "packingQty",
        "safetyDays",
        "minReqDay",
        "maxReqDays",
      ];

      if (value === "") {
        setItem((prev) => ({
          ...prev,
          [name]: 0,
        }));
      } else if (/^[0-9]\d*$/.test(value)) {
        if (limitedNumericFields.includes(name) && value.length > 5) {
          return; 
        }

        const numValue = Number(value);
        setItem((prev) => {
          const updatedItem = { ...prev, [name]: numValue };
          if (name === "minReqDay" || name === "maxReqDays" || name === "safetyDays" || name ==="stockPerDay" || name ==="packingQty") {
            setErrors((prev) => ({
              ...prev,
              minReqDay: "",
              maxReqDays: "",
              safetyDays: "",
              stockPerDay:"",
              packingQty:"",
              daysRelation: ""
            }));
          }
          
          return updatedItem;
        });
      }
    } else {

      if (!isEditing && shouldEnableAutoPopulate) {
        setShouldAutoPopulateExternal(false);
      }

      setItem((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    if (!isEditing && shouldEnableAutoPopulate) {
      setShouldAutoPopulateExternal(false);
    }

    if (name === "selfLife") {
      const boolValue = selectedOption?.value === "true";
      setItem((prev) => ({
        ...prev,
        [name]: boolValue,
        expiryDays: boolValue ? prev.expiryDays : 0,
      }));
    } else if (name === "model") {
      setSelectedModel(selectedOption);
      setItem((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else if (name === "category") {
      setSelectedCategory(selectedOption);
      setItem((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else if (name === "uom") {
      setSelectedUom(selectedOption);
      setItem((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setItem((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    if (!isEditing && shouldEnableAutoPopulate) {
      setShouldAutoPopulateExternal(false);
    }

    setItem((prev) => ({
      ...prev,
      [name]: checked,
    }));

    if (
      (name === "purchaseItem" ||
        name === "sellingItem" ||
        name === "productionItem") &&
      checked
    ) {
      setErrors((prev) => ({ ...prev, itemType: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!item.hsnCode || item.hsnCode.length < 1 || item.hsnCode.length > 8) {
      newErrors["hsnCode"] = "Required";
    }

    if (!item.internalPartNo || item.internalPartNo.length > 15) {
      newErrors["internalPartNo"] = "Required";
    }

    if (!item.externalPartNo || item.externalPartNo.length > 15) {
      newErrors["externalPartNo"] = "Required";
    }

    if (!item.partName) {
      newErrors["partName"] = "Required";
    }


    if (!selectedModel) {
      newErrors["model"] = "Select model type";
    }

    if (!selectedCategory) {
      newErrors["category"] = "Select category type";
    }

    if (!selectedUom) {
      newErrors["uom"] = "Select UOM type";
    }

    const numericFields: (keyof ItemType)[] = [
      "stockPerDay",
      "packingQty",
      "safetyDays",
      "minReqDay",
      "maxReqDays",
      "expiryDays",
    ];
    
    numericFields.forEach((field) => {
      if (field === "expiryDays" && !item.selfLife) return;

      const value = item[field];
      if (value === 0 || value === "") {
        newErrors[field] = "Required";
      } else if (field !== "expiryDays" && String(value).length > 5) {
        newErrors[field] = "Maximum 5 digits allowed";
      }
    });
    
    if (
      item.minReqDay && 
      item.safetyDays && 
      !newErrors["minReqDay"] && 
      !newErrors["safetyDays"] && 
      item.minReqDay >= item.safetyDays
    ) {
      newErrors["minReqDay"] = "Min Req Days must be less than Safety Days";
    }

    if (
      item.maxReqDays && 
      item.safetyDays && 
      !newErrors["maxReqDays"] && 
      !newErrors["safetyDays"] && 
      item.maxReqDays <= item.safetyDays
    ) {
      newErrors["maxReqDays"] = "Max Req Days must be greater than Safety Days";
    }

    if (!item.purchaseItem && !item.sellingItem && !item.productionItem) {
      newErrors["itemType"] = "Select item type";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isProcessing) {
      return;
    }
    
    setIsSubmitting(true);
    
    if (validateForm()) {
      const itemToSave = {
        ...item,
        model: selectedModel?.value || "",
        category: selectedCategory?.value || "",
        uom: selectedUom?.value || ""
      };
      onSave(itemToSave);
    } else {
      setIsSubmitting(false);
    }
  };

  const preventScroll = (e: WheelEvent) => {
    e.preventDefault();
  };

  const selfLifeOptions = [
    { value: "false", label: "No" },
    { value: "true", label: "Yes" },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <>
      <div className="breadcrumb">
        <span className="breadcrumb-item" onClick={onCancel}>
          Item
        </span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-current">
          {isEditing ? "Edit Item" : "Add New Item"}
        </span>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-item-form" noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label>
                HSN Code :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="hsnCode"
                value={item.hsnCode}
                onChange={handleChange}
                placeholder="max 8-digit HSN Code"
                maxLength={8}
                className={errors.hsnCode ? "error-input" : ""}
              />
              {errors.hsnCode && (
                <span className="error-message">{errors.hsnCode}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Internal Part No :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="internalPartNo"
                value={item.internalPartNo}
                onChange={handleChange}
                placeholder="Max 15 characters"
                maxLength={15}
                className={errors.internalPartNo ? "error-input" : ""}
              />
              {errors.internalPartNo && (
                <span className="error-message">{errors.internalPartNo}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                External Part No :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="externalPartNo"
                value={item.externalPartNo}
                onChange={handleChange}
                placeholder="Max 15 characters"
                maxLength={15}
                className={errors.externalPartNo ? "error-input" : ""}
              />
              {errors.externalPartNo && (
                <span className="error-message">{errors.externalPartNo}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Part Name :<span className="required">*</span>
              </label>
              <input
                type="text"
                name="partName"
                placeholder="Enter Part name"
                value={item.partName}
                onChange={handleChange}
                className={errors.partName ? "error-input" : ""}
              />
              {errors.partName && (
                <span className="error-message">{errors.partName}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Model :<span className="required">*</span>
              </label>
              <Select
                name="model"
                options={modelOptions}
                value={selectedModel}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchModels(inputValue);
                }}
                onChange={(option) => handleSelectChange(option, { name: "model" })}
                placeholder="Select Model"
                className={`react-select-container ${
                  errors.model ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.model && (
                <div className="error-message">{errors.model}</div>
              )}
            </div>
            <div className="form-group">
              <label>
                Category :<span className="required">*</span>
              </label>
              <Select
                name="category"
                options={categoryOptions}
                value={selectedCategory}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchCategories(inputValue);
                }}
                onChange={(option) => handleSelectChange(option, { name: "category" })}
                placeholder="Select Category"
                className={`react-select-container ${
                  errors.category ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.category && (
                <div className="error-message">{errors.category}</div>
              )}
            </div>

            {/* UOM Dropdown */}
            <div className="form-group">
              <label>
                UOM :<span className="required">*</span>
              </label>
              <Select
                name="uom"
                options={uomOptions}
                value={selectedUom}
                onInputChange={(inputValue) => {
                  setSearchTerm(inputValue);
                  fetchUoms(inputValue);
                }}
                onChange={(option) => handleSelectChange(option, { name: "uom" })}
                placeholder="Select UOM"
                className={`react-select-container ${
                  errors.uom ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.uom && <div className="error-message">{errors.uom}</div>}
            </div>
            <div className="checkbox-group">
              <label>
                Item Type :<span className="required item-type">*</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="purchaseItem"
                  checked={item.purchaseItem}
                  onChange={handleCheckboxChange}
                />{" "}
                Purchase
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="sellingItem"
                  checked={item.sellingItem}
                  onChange={handleCheckboxChange}
                />{" "}
                Selling
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="productionItem"
                  checked={item.productionItem}
                  onChange={handleCheckboxChange}
                />{" "}
                Production
              </label>
              {errors.itemType && (
                <div className="error-message">{errors.itemType}</div>
              )}
            </div>

            <div className="form-group">
              <label>
                Stock Per Day :<span className="required">*</span>
              </label>
              <input
                type="number"
                name="stockPerDay"
                value={item.stockPerDay || ""}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter Stock Per Day"
                className={errors.stockPerDay ? "error-input" : ""}
                max="99999" 
              />
              {errors.stockPerDay && (
                <span className="error-message">{errors.stockPerDay}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Packing Quantity :<span className="required">*</span>
              </label>
              <input
                type="number"
                name="packingQty"
                value={item.packingQty || ""}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter Packing Quantity"
                className={errors.packingQty ? "error-input" : ""}
                max="99999"
              />
              {errors.packingQty && (
                <span className="error-message">{errors.packingQty}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Safety Days :<span className="required">*</span>
              </label>
              <input
                type="number"
                name="safetyDays"
                value={item.safetyDays || ""}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter Safety Days"
                className={errors.safetyDays ? "error-input" : ""}
                max="99999"
              />
              {errors.safetyDays && (
                <span className="error-message">{errors.safetyDays}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Min Req Days :<span className="required">*</span>
              </label>
              <input
                type="number"
                name="minReqDay"
                value={item.minReqDay || ""}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter Min Req Days"
                className={errors.minReqDay ? "error-input" : ""}
                max="99999" 
              />
              {errors.minReqDay && (
                <span className="error-message">{errors.minReqDay}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Max Req Days :<span className="required">*</span>
              </label>
              <input
                type="number"
                name="maxReqDays"
                value={item.maxReqDays || ""}
                placeholder="Enter Max Req Days"
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                className={errors.maxReqDays ? "error-input" : ""}
                max="99999" 
              />
              {errors.maxReqDays && (
                <span className="error-message">{errors.maxReqDays}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Self Life :<span className="required">*</span>
              </label>
              <Select
                name="selfLife"
                options={selfLifeOptions}
                value={selfLifeOptions.find(
                  (option) =>
                    option.value === (item.selfLife ? "true" : "false")
                )}
                onChange={handleSelectChange}
                placeholder="Select Self Life"
                className={`react-select-container ${
                  errors.selfLife ? "error-select" : ""
                }`}
                classNamePrefix="react-select"
              />
              {errors.selfLife && (
                <span className="error-message">{errors.selfLife}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Expiry Days :
                {item.selfLife && <span className="required">*</span>}
              </label>
              <input
                type="number"
                name="expiryDays"
                value={item.selfLife ? item.expiryDays || "" : 0}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.addEventListener("wheel", preventScroll, {
                    passive: false,
                  })
                }
                onBlur={(e) =>
                  e.target.removeEventListener("wheel", preventScroll)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                disabled={!item.selfLife}
                readOnly={!item.selfLife}
                className={
                  errors.expiryDays && item.selfLife ? "error-input" : ""
                }
                placeholder={
                  !item.selfLife
                    ? "Enable Self Life to edit"
                    : "Enter Expiry Days"
                }
              />
              {errors.expiryDays && item.selfLife && (
                <span className="error-message">{errors.expiryDays}</span>
              )}
            </div>

            {isEditing && (
              <div className="form-group">
                <label>
                  Status :<span className="required">*</span>
                </label>
                <Select
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find(
                    (option) => option.value === item.status
                  )}
                  onChange={handleSelectChange}
                  placeholder="Select Status"
                  className={`react-select-container ${
                    errors.status ? "error-select" : ""
                  }`}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: "none",
                      overflow: "visible",
                    }),
                  }}
                />
                {errors.status && (
                  <span className="error-message">{errors.status}</span>
                )}
              </div>
            )}
          </div>

          <div className="button-container">
            <button
              type="button"
              className="button cancel-button"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button save-button"
              disabled={isProcessing || isSubmitting}
            >
              {isProcessing || isSubmitting
                ? "Saving..."
                : isEditing
                ? "Update Item"
                : "Add Item"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddItemForm;